import {ChangeDetectorRef, Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2} from "@angular/core";
import {PageMode, ResponsiveService} from "./responsive.service";
import {Subscription} from "rxjs";
import {NavigationService} from "./navigation.service";
import {SimpleChanges} from "@angular/core/src/metadata/lifecycle_hooks";

@Directive({
    selector: "[woa-responsive]"
})
/**
 * Directive to add responsive classes to an element
 */
export class ResponsiveDirective implements OnInit, OnChanges, OnDestroy {
    @Input() pillarboxedOnMobile: "mobile" | "none" = "none";
    @Input() pillarboxedOnDesktop: "mobile" | "desktop" | "double-mobile" | "none" = "none";
    @Input() fontScale: number;

    private subscriptions: Subscription = new Subscription();
    private initialised: boolean = false;

    constructor(private element: ElementRef,
                private responsiveService: ResponsiveService,
                private navigationService: NavigationService,
                private changeDetectorRef: ChangeDetectorRef,
                private renderer: Renderer2) {
    }

    ngOnInit(): void {
        if (this.fontScale !== undefined) {
            this.setFontSize();
        }

        if (this.responsiveService.isSafari) {
            this.renderer.addClass(this.element.nativeElement, "safari");
        } else if (this.responsiveService.isChrome) {
            this.renderer.addClass(this.element.nativeElement, "chrome");
        } else if (this.responsiveService.isEdge) {
            this.renderer.addClass(this.element.nativeElement, "edge");
        } else if (this.responsiveService.isFirefox) {
            this.renderer.addClass(this.element.nativeElement, "firefox");
        }

        this.subscriptions.add(this.responsiveService.mode$.subscribe(() => {
            this.applyMode();
        }));
        this.subscriptions.add(this.responsiveService.windowWidth$.subscribe(() => {
            this.setAvailableWidth();
            if (this.fontScale !== undefined) {
                this.setFontSize();
            }
        }));
        this.subscriptions.add(this.responsiveService.windowHeight$.subscribe(() => this.setAvailableHeight()));

        this.setAvailableWidth();
        this.setAvailableHeight();

        if (this.fontScale !== undefined) {
            // noinspection JSUnresolvedReference
            new (window as any).ResizeObserver(() => {
                this.setFontSize();
            }).observe(this.element.nativeElement);
            this.subscriptions.add(this.navigationService.pageCreated$.subscribe(() => {
                this.setFontSize();
            }));
        }

        this.initialised = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialised) {
            return;
        }
        if (changes["pillarboxedOnMobile"] || changes["pillarboxedOnDesktop"]) {
            this.setAvailableWidth();
        }
        if (changes["fontScale"]) {
            this.setFontSize();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private applyMode(): void {
        if (this.responsiveService.mobile) {
            this.renderer.removeClass(this.element.nativeElement, "desktop");
            this.renderer.addClass(this.element.nativeElement, "mobile");
        } else {
            this.renderer.removeClass(this.element.nativeElement, "mobile");
            this.renderer.addClass(this.element.nativeElement, "desktop");
        }

        this.renderer.removeClass(this.element.nativeElement, "narrow");
        this.renderer.removeClass(this.element.nativeElement, "basic");
        this.renderer.removeClass(this.element.nativeElement, "wide");
        this.renderer.removeClass(this.element.nativeElement, "wider");
        this.renderer.removeClass(this.element.nativeElement, "widest");

        this.renderer.removeClass(this.element.nativeElement, "small");
        this.renderer.removeClass(this.element.nativeElement, "medium");
        this.renderer.removeClass(this.element.nativeElement, "large");

        this.renderer.removeClass(this.element.nativeElement, "short");

        if (this.responsiveService.desktop) {
            // On desktop, we determine the page mode based on the desktop pillarboxing width
            const availableWidth = this.responsiveService.calculateAvailableWidth(this.element.nativeElement, this.pillarboxedOnDesktop);
            const mode: PageMode = this.responsiveService.calculateMode(availableWidth);
            const responsiveClass: string =
                mode === PageMode.NARROW_DESKTOP ? "narrow"
                    : mode === PageMode.BASIC_DESKTOP ? "basic"
                        : mode === PageMode.WIDE_DESKTOP ? "wide"
                            : mode === PageMode.WIDER_DESKTOP ? "wider"
                                : mode === PageMode.WIDEST_DESKTOP ? "widest"
                                    : undefined;
            if (responsiveClass) {
                this.renderer.addClass(this.element.nativeElement, responsiveClass);
            }
            if (this.responsiveService.short) {
                this.renderer.addClass(this.element.nativeElement, "short");
            }
        } else {
            // On mobile, we determine the page mode based on the actual window width
            const mode: PageMode = this.responsiveService.calculateMode(this.responsiveService.windowWidth);
            const responsiveClass: string =
                mode === PageMode.SMALL_MOBILE ? "small"
                    : mode === PageMode.MEDIUM_MOBILE ? "medium"
                        : mode === PageMode.MOBILE ? "large"
                            : undefined;
            if (responsiveClass) {
                this.renderer.addClass(this.element.nativeElement, responsiveClass);
            }
        }
        this.changeDetectorRef.markForCheck();

        if (this.fontScale !== undefined) {
            this.setFontSize();
        }
    }

    private setAvailableWidth(): void {
        this.element.nativeElement.style.removeProperty("--available-width");
        this.element.nativeElement.style.removeProperty("--pillarbox-gutter-width");
        this.renderer.removeClass(this.element.nativeElement, "pillarboxed");

        const availableWidth = this.responsiveService.calculateAvailableWidth(this.element.nativeElement, this.responsiveService.desktop ? this.pillarboxedOnDesktop : this.pillarboxedOnMobile);
        this.element.nativeElement.style.setProperty("--available-width", availableWidth + "px");
        if (this.responsiveService.desktop && this.pillarboxedOnDesktop && this.pillarboxedOnDesktop !== "none") {
            const gutterWidth: number = (this.responsiveService.windowWidth - availableWidth) / 2;
            this.element.nativeElement.style.setProperty("--pillarbox-gutter-width", gutterWidth + "px");
            if (gutterWidth) {
                this.renderer.addClass(this.element.nativeElement, "pillarboxed");
            }
        }
        this.changeDetectorRef.markForCheck();
    }

    private setAvailableHeight(): void {
        this.element.nativeElement.style.setProperty("--available-height", this.responsiveService.availableHeight + "px");
        this.changeDetectorRef.markForCheck();
    }

    private setFontSize(): void {
        if (this.fontScale) {
            setTimeout(() => {
                const fontSize: string = (this.element.nativeElement.offsetWidth * this.fontScale) + "px";
                this.element.nativeElement.style.setProperty("font-size", fontSize);
                this.element.nativeElement.style.setProperty("--woa-font-size", fontSize);
                this.changeDetectorRef.markForCheck();
            });
        } else {
            this.element.nativeElement.style.removeProperty("font-size");
            this.element.nativeElement.style.removeProperty("--woa-font-size");
            this.changeDetectorRef.markForCheck();
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/orchestration/responsive.directive.ts